import { useEffect } from 'react';
import { ToastContainer, ToastOptions, toast } from 'react-toastify';

import { type FlyThemeColor } from '@fly/core';
import { TOAST_EVENT_NAME, ToastEvent } from '@fly/shared';

import { FlyToast } from './FlyToast';

type ToastConfig = {
  color: FlyThemeColor;
  className: string;
  icon: string | null;
  toastMethod: (
    content: React.ReactNode,
    options?: ToastOptions
  ) => string | number;
};

const toastConfigs: Record<ToastEvent['type'], ToastConfig> = {
  custom: {
    color: 'neutral',
    className: 'bg-gray-3',
    toastMethod: toast,
    icon: null,
  },
  neutral: {
    color: 'neutral',
    className: 'bg-gray-3',
    icon: 'info_circled',
    toastMethod: toast,
  },
  error: {
    color: 'error',
    icon: 'cross_circled',
    className: 'bg-red-3',
    toastMethod: toast.error,
  },
  success: {
    color: 'success',
    className: 'bg-green-3',
    icon: 'check_circled',
    toastMethod: toast.success,
  },
  info: {
    color: 'info',
    className: 'bg-blue-3',
    icon: 'info_circled',
    toastMethod: toast.info,
  },
  warning: {
    color: 'warning',
    icon: 'warning',
    className: 'bg-yellow-3',
    toastMethod: toast.warn,
  },
};

export function FlyToastContainer() {
  useEffect(() => {
    const handleToastEvent = (event: Event) => {
      const { detail } = event as CustomEvent<ToastEvent>;

      const toastConfig = toastConfigs[detail.type];
      if (!toastConfig) {
        return;
      }

      const toastId = toastConfig.toastMethod(
        <FlyToast
          size={detail.size}
          variant={detail.variant}
          color={toastConfig.color}
          icon={detail.icon ?? toastConfig.icon}
          className={toastConfig.className}
          onClose={() => toast.dismiss(toastId)}
        >
          {detail.content}
        </FlyToast>,
        detail.toastOptions
      );
    };

    window.addEventListener(
      TOAST_EVENT_NAME as keyof WindowEventMap,
      handleToastEvent
    );

    return () => {
      window.removeEventListener(
        TOAST_EVENT_NAME as keyof WindowEventMap,
        handleToastEvent
      );
    };
  }, []);

  return (
    <ToastContainer
      containerId='custom'
      limit={1}
      toastClassName={() => 'p-0 mt-1 mb-1 z-50'}
      closeButton={false}
      icon={false}
      hideProgressBar
    />
  );
}
